/* Catálogo do modo demonstração: mesmo formato de GET /wl/catalogo, com uma empresa fictícia. */

import type { Campo, Catalogo } from '../tipos/api';
import { OPCOES_GRAFICOS } from './graficos';
import { MATERIAIS } from './materiais';

export { MATERIAIS, type MaterialDemo } from './materiais';

const DICA_COMB = 'Líquidos em L; GLP em kg; GNV em m³.';

const CAMPOS_DOCUMENTO: Campo[] = [
  { nome: 'data', rotulo: 'Data', tipo: 'data', obrigatorio: true, opcoes: [], padrao: null, dica: '' },
  { nome: 'descricao', rotulo: 'Descrição', tipo: 'texto', obrigatorio: true, opcoes: [], padrao: null, dica: '' },
  { nome: 'id_unidade', rotulo: 'Obra', tipo: 'obra', obrigatorio: true, opcoes: [], padrao: null, dica: 'Unidade de negócio cadastrada para a empresa.' },
  { nome: 'cnpj_fornecedor', rotulo: 'CNPJ do fornecedor', tipo: 'texto', obrigatorio: false, opcoes: [], padrao: null, dica: '' },
  { nome: 'link_referencia', rotulo: 'Link do documento', tipo: 'texto', obrigatorio: false, opcoes: [], padrao: null, dica: '' },
];

const cp = (nome: string, rotulo: string, extra: Partial<Campo> = {}): Campo => ({
  nome, rotulo, tipo: 'numero', obrigatorio: true, opcoes: [], padrao: null, dica: '', ...extra,
});

const categorias = [
  {
    slug: 'combustao-estacionaria', id_categoria: 4, escopo: 1, nome: 'Combustão estacionária',
    descricao: 'Geradores, caldeiras e equipamentos fixos do canteiro.',
    campos: [
      cp('id_combustivel', 'Combustível', { tipo: 'lista:combustiveis' }), cp('consumo', 'Consumo no período'),
      cp('unidade', 'Unidade', { tipo: 'opcoes', opcoes: ['L', 'kg', 't', 'm³'], padrao: 'L', dica: 'Líquidos (diesel, gasolina, etanol) em L; GLP em kg ou t; gás natural em m³.' }),
      cp('id_setor', 'Setor', { tipo: 'lista:setores', padrao: 2 }),
    ],
  },
  {
    slug: 'combustao-movel-2', id_categoria: 2, escopo: 1, nome: 'Combustão móvel — por combustível',
    descricao: 'Máquinas, caminhões e frota própria, informando o combustível consumido.',
    campos: [cp('id_combustivel', 'Combustível', { tipo: 'lista:combustiveis' }), cp('consumo_mensal', 'Consumo no período', { dica: DICA_COMB })],
  },
  {
    slug: 'combustao-movel-1', id_categoria: 1, escopo: 1, nome: 'Combustão móvel — por tipo de veículo',
    descricao: 'Frota própria, informando tipo e ano do veículo.',
    campos: [cp('id_veiculo', 'Tipo de veículo', { tipo: 'lista:veiculos' }), cp('ano_veiculo', 'Ano da frota', { padrao: 2020 }), cp('consumo_mensal', 'Consumo no período (L)')],
  },
  {
    slug: 'emissoes-fugitivas-1', id_categoria: 5, escopo: 1, nome: 'Emissões fugitivas (gases refrigerantes)',
    descricao: 'Recarga de ar-condicionado e refrigeração dos escritórios de obra.',
    campos: [
      cp('id_gas', 'Gás refrigerante', { tipo: 'lista:gases' }), cp('existentes_recarga', 'Recarga de equipamentos existentes (kg)'),
      cp('novas_carga', 'Carga de unidades novas (kg)', { obrigatorio: false }), cp('novas_capacidade', 'Capacidade de unidades novas (kg)', { obrigatorio: false }),
      cp('dispensadas_capacidade', 'Capacidade de unidades descartadas (kg)', { obrigatorio: false }), cp('dispensadas_recuperada', 'Gás recuperado no descarte (kg)', { obrigatorio: false }),
    ],
  },
  {
    slug: 'energia-localizacao', id_categoria: 14, escopo: 2, nome: 'Energia elétrica da rede',
    descricao: 'Abordagem de localização — fator médio mensal do SIN.',
    campos: [cp('consumo_mensal', 'Consumo no período (MWh)', { dica: 'O fator do SIN é por MWh: divida o consumo em kWh da fatura por 1.000.' })],
  },
  {
    slug: 'bens-servicos-comprados', id_categoria: 38, escopo: 3, nome: 'Bens e serviços comprados',
    descricao: 'Materiais comprados para a obra (concreto, aço, cimento, blocos…), pelo fator de cada material.',
    campos: [
      cp('id_material', 'Material', { tipo: 'lista:materiais' }), cp('quantidade', 'Quantidade comprada'),
      cp('unidade', 'Unidade da quantidade', { tipo: 'opcoes', opcoes: ['t', 'kg', 'm³', 'L', 'm', 'm²'], obrigatorio: false, dica: 'Em branco, vale a unidade do material. Massa ↔ volume só converte se o material tiver densidade.' }),
      cp('valor_total', 'Valor da nota (R$)', { obrigatorio: false }),
    ],
  },
  {
    slug: 'transporte-upstream-2', id_categoria: 29, escopo: 3, nome: 'Transporte de materiais (upstream)',
    descricao: 'Frete contratado para trazer materiais à obra, pelo combustível consumido.',
    campos: [cp('id_combustivel', 'Combustível', { tipo: 'lista:combustiveis' }), cp('consumo_mensal', 'Consumo no período', { dica: DICA_COMB })],
  },
  {
    slug: 'transporte-downstream-2', id_categoria: 25, escopo: 3, nome: 'Transporte de resíduos e entregas (downstream)',
    descricao: 'Retirada de entulho e entregas contratadas, pelo combustível consumido.',
    campos: [cp('id_combustivel', 'Combustível', { tipo: 'lista:combustiveis' }), cp('consumo_mensal', 'Consumo no período', { dica: DICA_COMB })],
  },
  {
    slug: 'residuos-ipcc', id_categoria: 39, escopo: 3, nome: 'Resíduos — IPCC',
    descricao: 'Resíduos gerados na obra (entulho, madeira, sucata, orgânicos…) pelo tipo e pelo destino.',
    campos: [
      cp('id_residuo', 'Tipo de resíduo', { tipo: 'lista:residuos', dica: 'Pelo nome da lista ou por uma descrição parecida (ex.: Sucata metálica).' }),
      cp('id_destino', 'Destino', { tipo: 'lista:destinos_residuos' }),
      cp('quantidade', 'Quantidade'),
      cp('unidade', 'Unidade', { tipo: 'opcoes', opcoes: ['t', 'kg'], padrao: 't' }),
    ],
  },
  {
    slug: 'efluentes-ipcc-dbo', id_categoria: 40, escopo: 3, nome: 'Efluentes — IPCC (DBO)',
    descricao: 'Esgoto do canteiro e dos escritórios pelo volume, carga orgânica e tipo de tratamento.',
    campos: [
      cp('id_tratamento', 'Tipo de tratamento', { tipo: 'lista:tratamentos_efluentes', dica: 'Define o MCF do cálculo (ex.: fossa séptica 0,5; reator anaeróbio 0,8).' }),
      cp('volume_m3', 'Volume de efluente (m³)'),
      cp('dbo_kg_m3', 'Carga orgânica (kg DBO/m³)', { padrao: 0.4, dica: 'Esgoto doméstico típico: 0,4 kg DBO/m³.' }),
      cp('maximo_ch4', 'Capacidade máxima de CH₄ (kg CH₄/kg DBO)', { obrigatorio: false, padrao: 0.6 }),
    ],
  },
];

/** Amostra de backend.lista_fatores_emissao_residuos_ipcc_co2 (mesmos ids). */
export const RESIDUOS_DEMO = [
  { id: 1, nome: 'Ambulatorial' }, { id: 5, nome: 'Eletrônico' }, { id: 7, nome: 'Entulho resíduo construção civil' },
  { id: 9, nome: 'EPI/EPC usado' }, { id: 14, nome: 'Isopor' }, { id: 15, nome: 'Lâmpadas' }, { id: 19, nome: 'Madeira contaminada' },
  { id: 20, nome: 'Massa verde' }, { id: 25, nome: 'Pneus' }, { id: 27, nome: 'Resíduo orgânico' }, { id: 28, nome: 'Solo contaminado' },
  { id: 29, nome: 'Sucata Metálica' }, { id: 33, nome: 'Varrição' }, { id: 34, nome: 'Vidro' }, { id: 36, nome: 'Papel/papelão' },
  { id: 37, nome: 'Plástico' }, { id: 40, nome: 'Óleo lubrificante' }, { id: 41, nome: 'Tintas' }, { id: 50, nome: 'Lodo ETE' },
  { id: 71, nome: 'Madeira sem tratamento' }, { id: 76, nome: 'Resíduo vegetal, podas de árvores' },
];

/** Fator aproximado (tCO₂e por t) por destino, só para estimar no modo demonstração. */
export const FATOR_DESTINO_DEMO: Record<number, number> = { 1: 0.6, 2: 0.8, 3: 0.17, 4: 0.1, 5: 0.9, 6: 0.9, 7: 1.2, 8: 0 };

export const DEMO_CATALOGO: Catalogo = {
  empresa: { id: 'demo', nome: 'Construtora Aurora Engenharia', cnpj: '00000000000191', versao_ghg: 'v2025.0.1' },
  obras: ['Edifício Corporate Tower', 'Galpão Logístico Norte', 'Condomínio Vista Verde', 'Residencial Aurora', 'Shopping Parque Sul']
    .map((nome, i) => ({ id: `obra-${i + 1}`, nome, nivel: 1 })),
  categorias: categorias.map((c) => ({ ...c, campos: [...CAMPOS_DOCUMENTO, ...c.campos] })),
  listas: {
    combustiveis: [
      { id: 2, nome: 'Etanol' }, { id: 3, nome: 'Gás Liquefeito de Petróleo (GLP)' }, { id: 33, nome: 'Gás Natural Seco' },
      { id: 5, nome: 'Gasolina Automotiva (comercial)' }, { id: 6, nome: 'Óleo Diesel (comercial)' },
    ],
    veiculos: [
      { id: 1, nome: 'Automóvel a gasolina' }, { id: 3, nome: 'Automóvel flex a gasolina' },
      { id: 16, nome: 'Ônibus urbano a diesel' }, { id: 15, nome: 'Ônibus rodoviário a diesel' },
    ],
    gases: [{ id: 5, nome: 'HFC-32' }, { id: 9, nome: 'HFC-134a' }, { id: 58, nome: 'R-410A' }],
    setores: [
      { id: 1, nome: 'Energia' }, { id: 2, nome: 'Manufatura ou Construção' },
      { id: 3, nome: 'Comercial ou Institucional' }, { id: 4, nome: 'Residencial, Agricultura, Florestal ou Pesca' },
    ],
    materiais: MATERIAIS,
    residuos: [...RESIDUOS_DEMO].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    destinos_residuos: [
      { id: 1, nome: 'Aterro' }, { id: 2, nome: 'Lixão' }, { id: 3, nome: 'Compostagem' }, { id: 4, nome: 'Tratamento biológico' },
      { id: 5, nome: 'Incinerador' }, { id: 6, nome: 'Incinerador (camara)' }, { id: 7, nome: 'Queima a céu aberto' }, { id: 8, nome: 'Reciclagem' },
    ],
    tratamentos_efluentes: [
      { id: 2, nome: 'Fossa séptica', mcf: 0.5 }, { id: 8, nome: 'Fossas secas', mcf: 0.1 }, { id: 4, nome: 'Lagoa anaeróbia profunda (profundidade > 2 metros)', mcf: 0.8 },
      { id: 5, nome: 'Lagoa anaeróbia rasa (profundidade < 2 metros)', mcf: 0.2 }, { id: 3, nome: 'Reator anaeróbio', mcf: 0.8 },
      { id: 1, nome: 'Tratamento aeróbio (lodo ativado, lagoa aerada, etc)', mcf: 0.03 },
    ],
  },
  tipos_documento: [
    { id: 'combustivel', nome: 'Nota de combustível' },
    { id: 'energia', nome: 'Fatura de energia' },
    { id: 'material', nome: 'Nota de material' },
    { id: 'frete', nome: 'Frete / CT-e' },
    { id: 'outro', nome: 'Outro' },
  ],
  graficos: OPCOES_GRAFICOS,
};

/** Nome, categoria geral e escopo como em backend.categorias_emissao. */
export const CATEGORIAS_BACKEND: Record<number, [string, string, number]> = {
  4: ['Combustão Estacionária', 'Combustão Estacionária', 1],
  2: ['Combustão Móvel 2', 'Combustão Móvel', 1],
  1: ['Combustão Móvel 1', 'Combustão Móvel', 1],
  5: ['Emissões Fugitivas 1', 'Emissões Fugitivas', 1],
  14: ['Energia Elétrica (Localização)', 'Energia Elétrica (Localização)', 2],
  29: ['Transporte e Distribuição - Upstream 2', 'Transporte e Distribuição (Upstream)', 3],
  25: ['Transporte e Distribuição - Downstream 2', 'Transporte e Distribuição (Downstream)', 3],
  38: ['Bens e Serviços Comprados', 'Bens e Serviços Comprados', 3],
  39: ['Resíduos - IPCC', 'Resíduos Sólidos da Operação', 3],
  40: ['Efluentes - IPCC - DBO', 'Efluentes Gerados na Operação', 3],
};

/** Fator aproximado (tCO₂e por unidade), só para estimar no modo demonstração. */
export const FATOR_DEMO: Record<string, number> = {
  'combustao-estacionaria': 0.0027,
  'combustao-movel-2': 0.0027,
  'combustao-movel-1': 0.00255,
  'emissoes-fugitivas-1': 1.9,
  'energia-localizacao': 0.045,
  'transporte-upstream-2': 0.0027,
  'transporte-downstream-2': 0.0027,
};
